import { type ReactNode, createContext, createElement, useContext, useMemo } from "react";
import { readLandingHostProps } from "./host-props";

export type TLandingLinks = {
	customersHref: string;
	getFundingHref: string;
	getInTouchHref: string;
	getStartedHref: string;
	loginHref: string;
	manageCashFlowHref: string;
	questionnaireHref: string;
	resourcesHref: string;
};

export const DEFAULT_LANDING_LINKS: TLandingLinks = {
	customersHref: "#customers",
	getFundingHref: "https://app.triffin.com/credit-apply/credit-facility",
	getInTouchHref: "#get-in-touch",
	getStartedHref: "#get-started",
	loginHref: "#login",
	manageCashFlowHref: "#manage-cash-flow",
	questionnaireHref: "/questionnaire",
	resourcesHref: "#resources",
};

export function mergeLandingLinks(overrides?: Partial<TLandingLinks>): TLandingLinks {
	if (!overrides) {
		return DEFAULT_LANDING_LINKS;
	}

	return {
		customersHref: overrides.customersHref ?? DEFAULT_LANDING_LINKS.customersHref,
		getFundingHref: overrides.getFundingHref ?? DEFAULT_LANDING_LINKS.getFundingHref,
		getInTouchHref: overrides.getInTouchHref ?? DEFAULT_LANDING_LINKS.getInTouchHref,
		getStartedHref: overrides.getStartedHref ?? DEFAULT_LANDING_LINKS.getStartedHref,
		loginHref: overrides.loginHref ?? DEFAULT_LANDING_LINKS.loginHref,
		manageCashFlowHref: overrides.manageCashFlowHref ?? DEFAULT_LANDING_LINKS.manageCashFlowHref,
		questionnaireHref: overrides.questionnaireHref ?? DEFAULT_LANDING_LINKS.questionnaireHref,
		resourcesHref: overrides.resourcesHref ?? DEFAULT_LANDING_LINKS.resourcesHref,
	};
}

function readLandingLinkOverrides(): Partial<TLandingLinks> | undefined {
	const parsed = readLandingHostProps();
	if (!parsed) {
		return undefined;
	}
	return parsed as Partial<TLandingLinks>;
}

let cachedLandingLinks: TLandingLinks | undefined;

const LandingLinksContext = createContext<TLandingLinks | null>(null);

export function LandingLinksProvider({
	links,
	children,
}: {
	links?: Partial<TLandingLinks>;
	children: ReactNode;
}) {
	const value = mergeLandingLinks(links);
	return createElement(LandingLinksContext.Provider, { value }, children);
}

export function useLandingLinks(): TLandingLinks {
	const provided = useContext(LandingLinksContext);
	return useMemo(() => {
		if (provided) {
			return provided;
		}
		if (!cachedLandingLinks) {
			cachedLandingLinks = mergeLandingLinks(readLandingLinkOverrides());
		}
		return cachedLandingLinks;
	}, [provided]);
}
